import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    User, Shield, Lock, Save, RefreshCw, AlertCircle, CheckCircle, XCircle,
    Mail, Smartphone, Eye, EyeOff
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import OtpVerificationModal from '../shared/OtpVerificationModal';

const PASSWORD_REQUIREMENTS = [
    { id: 'length', label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { id: 'uppercase', label: 'One uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { id: 'lowercase', label: 'One lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { id: 'number', label: 'One number', test: (pwd) => /[0-9]/.test(pwd) },
    { id: 'special', label: 'One special character (@#$%^&+=!)', test: (pwd) => /[@#$%^&+=!]/.test(pwd) },
];

// MFA Method Card
const MfaMethodCard = ({ icon: Icon, title, description, enabled, available, loading, onToggle, permanent }) => (
    <div className={`p-5 rounded-xl border-2 transition-all duration-200 ${
        enabled
            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-sm'
            : available
                ? 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                : 'bg-gray-50 border-gray-100'
    }`}>
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl transition-colors ${
                    enabled
                        ? 'bg-green-100 ring-2 ring-green-200'
                        : available
                            ? 'bg-gray-100'
                            : 'bg-gray-100/50'
                }`}>
                    <Icon className={`w-6 h-6 ${
                        enabled
                            ? 'text-green-600'
                            : available
                                ? 'text-gray-600'
                                : 'text-gray-400'
                    }`} />
                </div>

                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <h4 className={`font-semibold ${available ? 'text-gray-900' : 'text-gray-400'}`}>
                            {title}
                        </h4>
                        {enabled ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full">
                                <CheckCircle className="w-3.5 h-3.5" />
                                {permanent ? 'ALWAYS ON' : 'ENABLED'}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-500 rounded-full">
                                <XCircle className="w-3.5 h-3.5" />
                                DISABLED
                            </span>
                        )}
                    </div>
                    <p className={`text-sm ${available ? 'text-gray-500' : 'text-gray-400'}`}>{description}</p>
                </div>
            </div>

            {permanent && enabled ? (
                <span className="px-5 py-2.5 text-sm font-semibold text-green-700 bg-green-100 border-2 border-green-200 rounded-lg">
                    Always On
                </span>
            ) : (
                <button
                    onClick={onToggle}
                    disabled={loading || !available}
                    className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        enabled
                            ? 'text-red-600 bg-white border-2 border-red-200 hover:bg-red-50 hover:border-red-300'
                            : 'text-white bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                >
                    {loading ? (
                        <span className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Processing...
                        </span>
                    ) : enabled ? 'Disable' : 'Enable'}
                </button>
            )}
        </div>
    </div>
);

const ProfilePage = () => {
    const { user, updateUser } = useAuth();
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'profile';
    const [activeTab, setActiveTab] = useState(initialTab);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Profile state
    const [profileForm, setProfileForm] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phoneNumber: user?.phoneNumber || '',
    });
    const [originalProfile, setOriginalProfile] = useState(null);
    const [hasProfileChanges, setHasProfileChanges] = useState(false);

    // MFA state
    const [mfaStatus, setMfaStatus] = useState(null);
    const [mfaLoading, setMfaLoading] = useState({ email: false, sms: false });
    const [otpModal, setOtpModal] = useState({ open: false, channel: null, action: null });
    const [otpError, setOtpError] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);

    // Password state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [passwordChanging, setPasswordChanging] = useState(false);

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'password', label: 'Password', icon: Lock },
    ];

    // Fetch MFA status when security tab opens
    useEffect(() => {
        if (activeTab === 'security') {
            fetchMfaStatus();
        }
    }, [activeTab]);

    // Track profile changes
    useEffect(() => {
        if (originalProfile) {
            const changed = Object.keys(profileForm).some(
                key => profileForm[key] !== (originalProfile[key] || '')
            );
            setHasProfileChanges(changed);
        }
    }, [profileForm, originalProfile]);

    // Sync profile form from AuthContext (which now includes phoneNumber)
    useEffect(() => {
        const initial = {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            phoneNumber: user?.phoneNumber || '',
        };
        setProfileForm(initial);
        setOriginalProfile(initial);
    }, [user]);

    const fetchMfaStatus = async () => {
        try {
            const result = await authService.mfaStatus();
            if (result.success || result.data) {
                setMfaStatus(result.data || result);
            }
        } catch (err) {
            console.error('Failed to fetch MFA status:', err);
        }
    };

    const handleProfileSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            await authService.profile.update({
                firstName: profileForm.firstName,
                lastName: profileForm.lastName,
                phoneNumber: profileForm.phoneNumber,
            });
            updateUser({
                firstName: profileForm.firstName,
                lastName: profileForm.lastName,
                phoneNumber: profileForm.phoneNumber,
            });
            setOriginalProfile({ ...profileForm });
            setHasProfileChanges(false);
            setSuccess('Profile updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleProfileReset = () => {
        if (originalProfile) {
            setProfileForm({ ...originalProfile });
        }
    };

    // MFA toggle handler — per-channel endpoints
    const handleMfaToggle = async (channel) => {
        const isEnabled = channel === 'EMAIL' ? mfaStatus?.emailMfaEnabled : mfaStatus?.smsMfaEnabled;
        const action = isEnabled ? 'disable' : 'enable';
        const key = channel.toLowerCase();

        setMfaLoading(prev => ({ ...prev, [key]: true }));
        setError('');

        try {
            if (channel === 'EMAIL') {
                await authService.mfaEmailEnable();
            } else {
                await (isEnabled ? authService.mfaSmsDisable() : authService.mfaSmsEnable());
            }
            setOtpModal({ open: true, channel, action });
        } catch (err) {
            setError(err.message || `Failed to ${action} ${channel} MFA`);
        } finally {
            setMfaLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleOtpVerify = async (code) => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const { channel, action } = otpModal;
            if (channel === 'EMAIL') {
                await authService.mfaEmailEnableVerify(code);
            } else {
                await (action === 'enable' ? authService.mfaSmsEnableVerify(code) : authService.mfaSmsDisableVerify(code));
            }
            const actionLabel = action === 'enable' ? 'enabled' : 'disabled';
            setSuccess(`${channel} MFA ${actionLabel} successfully`);
            setOtpModal({ open: false, channel: null, action: null });
            await fetchMfaStatus();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setOtpError(err.message || 'Invalid verification code');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleOtpResend = async () => {
        const { channel, action } = otpModal;
        if (channel) {
            try {
                if (channel === 'EMAIL') {
                    await authService.mfaEmailEnable();
                } else {
                    await (action === 'enable' ? authService.mfaSmsEnable() : authService.mfaSmsDisable());
                }
            } catch {
                // silently fail resend
            }
        }
    };

    // Password change
    const passwordStrength = PASSWORD_REQUIREMENTS.map(req => ({
        ...req,
        passed: req.test(passwordForm.newPassword)
    }));
    const allRequirementsMet = passwordStrength.every(req => req.passed);
    const passwordsMatch = passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.confirmPassword.length > 0;
    const isPasswordFormValid = passwordForm.currentPassword && allRequirementsMet && passwordsMatch;

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!isPasswordFormValid) return;

        if (passwordForm.newPassword === passwordForm.currentPassword) {
            setError('New password must be different from current password');
            return;
        }

        setPasswordChanging(true);
        setError('');
        setSuccess('');

        try {
            await authService.changePassword(
                passwordForm.currentPassword,
                passwordForm.newPassword,
                passwordForm.confirmPassword
            );
            setSuccess('Password changed successfully');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            const message = err.message || '';
            if (message.toLowerCase().includes('current password')) {
                setError('Current password is incorrect');
            } else if (message.toLowerCase().includes('reuse') || message.toLowerCase().includes('recent')) {
                setError('Cannot reuse recent passwords');
            } else {
                setError(message || 'Failed to change password');
            }
        } finally {
            setPasswordChanging(false);
        }
    };

    return (
        <div>
            {/* Header Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {user?.firstName} {user?.lastName}
                        </h1>
                        <p className="text-gray-500">{user?.email}</p>
                        {user?.role && (
                            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full border border-blue-200">
                                {user.role}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Alerts */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-red-700">{error}</p>
                    <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
                        <XCircle className="w-4 h-4" />
                    </button>
                </div>
            )}
            {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-green-700">{success}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="border-b border-gray-200">
                    <div className="flex">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => { setActiveTab(tab.id); setError(''); setSuccess(''); }}
                                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                    activeTab === tab.id
                                        ? 'text-blue-600 border-blue-600'
                                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6">
                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="space-y-6 max-w-2xl">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={profileForm.firstName}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={profileForm.lastName}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={profileForm.phoneNumber}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                                </div>
                            </div>

                            {hasProfileChanges && (
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={handleProfileReset}
                                        className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleProfileSave}
                                        disabled={saving}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                        Save Changes
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Shield className="w-5 h-5 text-gray-600" />
                                <div>
                                    <h3 className="font-semibold text-gray-900">Two-Factor Authentication</h3>
                                    <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                                    {mfaStatus?.emailMfaEnabled && mfaStatus?.smsMfaEnabled && (
                                        <span className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                                            EMAIL & SMS Active
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4 max-w-2xl">
                                <MfaMethodCard
                                    icon={Mail}
                                    title="Email Authentication"
                                    description={`Receive codes via email to ${user?.email}`}
                                    enabled={mfaStatus?.emailMfaEnabled}
                                    available={true}
                                    loading={mfaLoading.email}
                                    onToggle={() => handleMfaToggle('EMAIL')}
                                    permanent={true}
                                />

                                <MfaMethodCard
                                    icon={Smartphone}
                                    title="SMS Authentication"
                                    description={profileForm.phoneNumber
                                        ? `Receive codes via SMS to ${profileForm.phoneNumber}`
                                        : 'Add a phone number in your profile to enable'}
                                    enabled={mfaStatus?.smsMfaEnabled}
                                    available={!!profileForm.phoneNumber}
                                    loading={mfaLoading.sms}
                                    onToggle={() => handleMfaToggle('SMS')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Password Tab */}
                    {activeTab === 'password' && (
                        <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.current ? 'text' : 'password'}
                                        value={passwordForm.currentPassword}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.new ? 'text' : 'password'}
                                        value={passwordForm.newPassword}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Password Requirements */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Password Requirements</p>
                                <div className="space-y-2">
                                    {passwordStrength.map((req) => (
                                        <div
                                            key={req.id}
                                            className={`flex items-center gap-2 text-sm ${
                                                passwordForm.newPassword.length === 0
                                                    ? 'text-gray-400'
                                                    : req.passed
                                                    ? 'text-green-600'
                                                    : 'text-red-500'
                                            }`}
                                        >
                                            {passwordForm.newPassword.length === 0 ? (
                                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                            ) : req.passed ? (
                                                <CheckCircle className="h-4 w-4" />
                                            ) : (
                                                <XCircle className="h-4 w-4" />
                                            )}
                                            <span>{req.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        value={passwordForm.confirmPassword}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition ${
                                            passwordForm.confirmPassword.length === 0
                                                ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                                                : passwordsMatch
                                                ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                                                : 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                        }`}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {passwordForm.confirmPassword.length > 0 && !passwordsMatch && (
                                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                        <XCircle className="h-4 w-4" />
                                        Passwords do not match
                                    </p>
                                )}
                                {passwordsMatch && (
                                    <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                                        <CheckCircle className="h-4 w-4" />
                                        Passwords match
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={passwordChanging || !isPasswordFormValid}
                                className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {passwordChanging ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Changing Password...
                                    </>
                                ) : (
                                    <>
                                        <Lock className="w-4 h-4" />
                                        Change Password
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* OTP Verification Modal */}
            <OtpVerificationModal
                isOpen={otpModal.open}
                onClose={() => {
                    setOtpModal({ open: false, channel: null, action: null });
                    setOtpError('');
                }}
                onVerify={handleOtpVerify}
                onResend={handleOtpResend}
                title={
                    otpModal.action === 'enable'
                        ? `Enable ${otpModal.channel} Authentication`
                        : `Disable ${otpModal.channel} Authentication`
                }
                description={
                    otpModal.channel === 'SMS'
                        ? `Enter the code sent to your phone`
                        : `Enter the code sent to ${user?.email}`
                }
                loading={otpLoading}
                error={otpError}
            />
        </div>
    );
};

export default ProfilePage;
